import { CartAddEvent } from '@theme/events';

class HDQuickAdd {
  constructor() {
    this.dialog = document.getElementById('hd-quick-add-modal');
    this.cache = new Map();
    this.product = null;
    this.trigger = null;
    this.selectedOptions = [];

    if (!this.dialog) return;

    this.nodes = {
      image: this.dialog.querySelector('[data-hd-quick-add-image]'),
      title: this.dialog.querySelector('[data-hd-quick-add-title]'),
      price: this.dialog.querySelector('[data-hd-quick-add-price]'),
      options: this.dialog.querySelector('[data-hd-quick-add-options]'),
      message: this.dialog.querySelector('[data-hd-quick-add-message]'),
      submit: this.dialog.querySelector('[data-hd-quick-add-submit]'),
      details: this.dialog.querySelector('[data-hd-quick-add-details]'),
      close: this.dialog.querySelector('[data-hd-quick-add-close]'),
    };

    document.addEventListener('click', this.onDocumentClick);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.nodes.close?.addEventListener('click', this.onCloseClick);
    this.nodes.submit?.addEventListener('click', this.onSubmit);
  }

  onDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-hd-quick-add-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.open(trigger);
  };

  onDialogClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target === this.dialog || target.closest('[data-hd-quick-add-close]')) {
      this.close();
      return;
    }

    const optionButton = target.closest('[data-hd-quick-add-option]');
    if (!(optionButton instanceof HTMLButtonElement)) return;

    const optionIndex = Number(optionButton.dataset.optionIndex);
    const value = optionButton.dataset.value || '';
    this.selectedOptions[optionIndex] = value;
    this.renderOptions();
    this.updateVariantState();
  };

  onCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.close();
  };

  onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  onSubmit = async () => {
    const variant = this.currentVariant();
    if (!variant || variant.available === false) {
      this.setMessage('Choose an available combination first.', 'error');
      return;
    }

    await this.addVariant(variant.id, this.product?.id);
  };

  async open(trigger) {
    this.trigger = trigger;
    this.setMessage('');

    const directVariantId = trigger.dataset.variantId;
    if (directVariantId) {
      await this.addVariant(directVariantId, trigger.dataset.productId);
      return;
    }

    this.setBusy(true);
    this.seedModalFromTrigger(trigger);
    this.dialog.showModal();

    try {
      this.product = await this.fetchProduct(trigger.dataset.productHandle, trigger.dataset.productUrl);
      this.selectedOptions = this.defaultSelectedOptions(this.product);
      this.renderProduct();
    } catch (error) {
      console.error(error);
      this.setMessage('Could not load options. Open the product page to choose your size.', 'error');
      if (this.nodes.details) this.nodes.details.hidden = false;
    } finally {
      this.setBusy(false);
    }
  }

  close() {
    if (!this.dialog.open) return;

    this.dialog.close();
    this.trigger?.focus?.({ preventScroll: true });
  }

  seedModalFromTrigger(trigger) {
    this.product = null;
    this.selectedOptions = [];
    if (this.nodes.title) this.nodes.title.textContent = trigger.dataset.productTitle || 'Choose options';
    if (this.nodes.price) this.nodes.price.textContent = trigger.dataset.productPrice || '';
    if (this.nodes.details) {
      this.nodes.details.href = trigger.dataset.productUrl || '#';
      this.nodes.details.hidden = !trigger.dataset.productUrl;
    }
    if (this.nodes.image) {
      this.nodes.image.src = trigger.dataset.productImage || '';
      this.nodes.image.alt = trigger.dataset.productTitle || '';
      this.nodes.image.hidden = !trigger.dataset.productImage;
    }
    if (this.nodes.options) {
      this.nodes.options.innerHTML = '<div class="hd-quick-add-modal__loading">Loading size and fit options...</div>';
    }
    this.updateSubmit(false, 'Loading...');
  }

  async fetchProduct(handle, url) {
    const productPath = handle ? `/products/${handle}.js` : `${url}.js`;
    if (this.cache.has(productPath)) return this.cache.get(productPath);

    const response = await fetch(productPath, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);

    const product = await response.json();
    this.cache.set(productPath, product);
    return product;
  }

  defaultSelectedOptions(product) {
    const availableVariant = product.variants?.find((variant) => variant.available) || product.variants?.[0];
    return availableVariant?.options ? [...availableVariant.options] : [];
  }

  renderProduct() {
    if (!this.product) return;

    if (this.nodes.title) this.nodes.title.textContent = this.product.title || 'Choose options';
    if (this.nodes.details) this.nodes.details.href = this.product.url || this.trigger?.dataset.productUrl || '#';
    if (this.nodes.image) {
      const image = this.product.featured_image || this.trigger?.dataset.productImage || '';
      this.nodes.image.src = image;
      this.nodes.image.alt = this.product.title || '';
      this.nodes.image.hidden = !image;
    }

    this.renderOptions();
    this.updateVariantState();
  }

  renderOptions() {
    if (!this.nodes.options || !this.product) return;

    const optionNames = this.product.options || [];
    this.nodes.options.innerHTML = optionNames
      .map((option, optionIndex) => {
        const optionName = typeof option === 'string' ? option : option?.name || `Option ${optionIndex + 1}`;
        const values = this.valuesForOption(optionIndex);
        const buttons = values
          .map((value) => {
            const selected = this.selectedOptions[optionIndex] === value;
            const available = this.optionCanLeadToAvailableVariant(optionIndex, value);
            return `
              <button
                type="button"
                class="hd-quick-add-modal__option${selected ? ' is-selected' : ''}"
                data-hd-quick-add-option
                data-option-index="${optionIndex}"
                data-value="${this.escapeAttr(value)}"
                aria-pressed="${selected ? 'true' : 'false'}"
                ${available ? '' : 'disabled'}
              >${this.escapeHtml(value)}</button>
            `;
          })
          .join('');

        return `
          <fieldset class="hd-quick-add-modal__option-group">
            <legend>${this.escapeHtml(optionName)}</legend>
            <div class="hd-quick-add-modal__option-list">${buttons}</div>
          </fieldset>
        `;
      })
      .join('');
  }

  valuesForOption(optionIndex) {
    const option = this.product?.options?.[optionIndex];
    if (Array.isArray(option?.values)) return option.values;

    const values = new Set();
    for (const variant of this.product?.variants || []) {
      if (variant.options?.[optionIndex]) values.add(variant.options[optionIndex]);
    }
    return [...values];
  }

  optionCanLeadToAvailableVariant(optionIndex, value) {
    return (this.product?.variants || []).some((variant) => {
      if (!variant.available) return false;
      return variant.options.every((optionValue, index) => {
        if (index === optionIndex) return optionValue === value;
        return !this.selectedOptions[index] || this.selectedOptions[index] === optionValue;
      });
    });
  }

  currentVariant() {
    if (!this.product || !this.selectedOptions.length) return null;
    return this.product.variants.find((variant) =>
      variant.options.every((optionValue, index) => optionValue === this.selectedOptions[index])
    );
  }

  updateVariantState() {
    const variant = this.currentVariant();
    const available = Boolean(variant?.available);

    if (this.nodes.price && variant?.price != null) {
      this.nodes.price.textContent = this.formatMoney(variant.price);
    }

    this.updateSubmit(available, available ? 'Add to cart' : 'Unavailable');
    this.setMessage(available ? 'Ready to add.' : 'Choose an available combination.', available ? '' : 'error');
  }

  async addVariant(variantId, productId) {
    this.setBusy(true);
    this.setMessage('Adding to cart...');

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add this item.');
      }

      document.dispatchEvent(
        new CartAddEvent(data, String(variantId), {
          source: 'product-form-component',
          itemCount: 1,
          productId,
          variantId: String(variantId),
          sections: data.sections || {},
        })
      );

      this.setMessage('Added to cart.', 'success');
      if (this.dialog.open) this.close();
      document.querySelector('cart-drawer-component')?.open?.();
    } catch (error) {
      console.error(error);
      this.setMessage(error?.message || 'Could not add this item. Please try again.', 'error');
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.dialog.toggleAttribute('aria-busy', busy);
    if (this.nodes.submit) this.nodes.submit.disabled = busy || this.nodes.submit.dataset.available !== 'true';
  }

  updateSubmit(enabled, label) {
    if (!this.nodes.submit) return;
    this.nodes.submit.dataset.available = enabled ? 'true' : 'false';
    this.nodes.submit.disabled = !enabled || this.dialog.hasAttribute('aria-busy');
    this.nodes.submit.textContent = label;
  }

  setMessage(message, state = '') {
    if (!this.nodes.message) return;
    this.nodes.message.textContent = message;
    this.nodes.message.dataset.state = state;
  }

  formatMoney(value) {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount)) return '';

    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount / 100);
  }

  escapeHtml(value = '') {
    return String(value).replace(/[&<>]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[char]);
  }

  escapeAttr(value = '') {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}

new HDQuickAdd();
